// Dictate-recipe review modal for the Manufacturing → Models mode.
//
// Entry-point change (2026-06-15): the dictation session no longer
// lives in the modal. The mic button inside the New Model form owns
// the session and hands a finished transcript to the panel, which
// mounts this modal pre-seeded. The modal therefore only owns the
// extract round-trip and the review/refine/save UI.
//
// State machine:
//   extracting    -> initial AI extraction in flight
//   extracted     -> review surface (refine chat + draft editor)
//   extractError  -> show error + fallback draft if any
//
// Plan reference: `docs/planning/mfg_recipe_verbal_authoring.md` §3.

import { useCallback, useEffect, useRef, useState } from "react";
import { mfgPrimaryBtnLgClass, mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { extractRecipeFromTranscript } from "@/lib/manufacturing/recipesDictationClient";
import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { MfgMaterial, MfgRecipeKind } from "@/lib/manufacturing/types";
import type { DictatedDraftLine, DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";
import type { MfgRecipeBulkCreateResponse } from "@/lib/manufacturing/types/recipeImport";
import { bulkCreateManufacturingRecipes } from "@/lib/manufacturingClient";
import { ManufacturingDictatedRecipeReview } from "./ManufacturingDictatedRecipeReview";

function lineToBulkPayload(line: DictatedDraftLine) {
  return {
    material_name:
      line.match.kind === "matched" ? line.match.canonical_name : line.material_name_heard,
    quantity: line.quantity,
    role: line.role,
    slot_label: normalizeSlotLabel(line.slot_label),
    upgrade_price_delta: line.upgrade_price_delta,
    swappable: line.role !== "structural",
  };
}

function normalizeDraftSlotLabels(draft: DictatedRecipeDraft): DictatedRecipeDraft {
  return {
    ...draft,
    lines: draft.lines.map((ln) => ({
      ...ln,
      slot_label: normalizeSlotLabel(ln.slot_label),
    })),
  };
}

function draftToBulkPayload(draft: DictatedRecipeDraft) {
  return {
    code: draft.code,
    name: draft.name,
    kind: draft.kind,
    tags: draft.tags,
    notes: "",
    lines: draft.lines.map(lineToBulkPayload),
  };
}

type ExtractionPhase =
  | { kind: "extracting" }
  | { kind: "extracted"; draft: DictatedRecipeDraft }
  | {
      kind: "extractError";
      message: string;
      draft: DictatedRecipeDraft | null;
    };

function applyDefaultKind(draft: DictatedRecipeDraft, defaultKind?: MfgRecipeKind): DictatedRecipeDraft {
  if (!defaultKind) return draft;
  return {
    ...draft,
    kind: defaultKind,
    detectedKind: {
      value: defaultKind,
      confidence: "high",
      reasoning: `Preset to ${defaultKind} for this authoring flow.`,
    },
  };
}

export function ManufacturingModelsDictateModal({
  transcript,
  materials,
  orgSlotLabels = [],
  defaultKind,
  onClose,
  onSaved,
}: {
  /** Final transcript captured by the New-Model mic button. The modal
   *  immediately runs extraction against this on mount. */
  transcript: string;
  /** Active material catalog — threaded into the review surface so
   *  the inline line picker can resolve matches in place. */
  materials: ReadonlyArray<MfgMaterial>;
  /** Org slot labels for draft line slot pickers. */
  orgSlotLabels?: readonly string[];
  /** When set (e.g. variant authoring), overrides extracted kind after extraction. */
  defaultKind?: MfgRecipeKind;
  onClose: () => void;
  onSaved: (summary: MfgRecipeBulkCreateResponse["summary"]) => void;
}) {
  const [extraction, setExtraction] = useState<ExtractionPhase>({ kind: "extracting" });
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      const result = await extractRecipeFromTranscript({ transcript });
      if (result.ok && result.data.ok && result.data.draft) {
        setExtraction({
          kind: "extracted",
          draft: normalizeDraftSlotLabels(applyDefaultKind(result.data.draft, defaultKind)),
        });
        return;
      }
      const message = result.ok
        ? "Extractor could not produce a usable draft. Close and re-dictate."
        : result.error || "Recipe extractor failed.";
      setExtraction({
        kind: "extractError",
        message,
        draft:
          result.ok && result.data.draft
            ? normalizeDraftSlotLabels(applyDefaultKind(result.data.draft, defaultKind))
            : null,
      });
    })();
  }, [transcript, defaultKind]);

  const handleSaveDraft = useCallback(
    async (draft: DictatedRecipeDraft) => {
      setSaveBusy(true);
      setSaveError(null);
      const result = await bulkCreateManufacturingRecipes({
        recipes: [draftToBulkPayload(draft)],
      });
      setSaveBusy(false);
      if (!result.ok) {
        setSaveError(result.error || "Could not save recipe.");
        return;
      }
      const summary = result.data.summary;
      const created = summary.created.length;
      if (created === 0) {
        const skip = summary.skipped[0];
        setSaveError(
          skip ? `Could not save: ${skip.reason}${skip.detail ? ` (${skip.detail})` : ""}.` : "Recipe was not created."
        );
        return;
      }
      onSaved(summary);
    },
    [onSaved]
  );

  return (
    <DictateModalShell onClose={onClose} title="Review dictated recipe">
      {extraction.kind === "extracting" ? (
        <div className="text-sm text-gray-700 dark:text-zinc-300">
          Extracting recipe from transcript…
        </div>
      ) : null}

      {extraction.kind === "extracted" ? (
        <ManufacturingDictatedRecipeReview
          transcript={transcript}
          draft={extraction.draft}
          materials={materials}
          orgSlotLabels={orgSlotLabels}
          onDraftChange={(next) =>
            setExtraction({ kind: "extracted", draft: next })
          }
          onRedictate={onClose}
          onClose={onClose}
          onSave={() => void handleSaveDraft(extraction.draft)}
          saveBusy={saveBusy}
          saveError={saveError}
        />
      ) : null}

      {extraction.kind === "extractError" ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/35 dark:text-red-200">
            {extraction.message}
          </div>
          {extraction.draft ? (
            <ManufacturingDictatedRecipeReview
              transcript={transcript}
              draft={extraction.draft}
              materials={materials}
              orgSlotLabels={orgSlotLabels}
              onDraftChange={(next) =>
                setExtraction({
                  kind: "extractError",
                  message: extraction.message,
                  draft: next,
                })
              }
              onRedictate={onClose}
              onClose={onClose}
              onSave={() => extraction.draft && void handleSaveDraft(extraction.draft)}
              saveBusy={saveBusy}
              saveError={saveError}
              fallback
            />
          ) : (
            <div className="flex justify-end gap-2">
              <button type="button" className={mfgSecondaryBtnClass} onClick={onClose}>
                Close
              </button>
              <button type="button" className={mfgPrimaryBtnLgClass} onClick={onClose}>
                Dictate again
              </button>
            </div>
          )}
        </div>
      ) : null}
    </DictateModalShell>
  );
}

function DictateModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
          {title}
        </h3>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}
